import { differenceInDays } from "date-fns";
import { formatPrice } from "./money";
import { hasPriceFeed } from "./types";
import type { ClosedTrade, Trade, TradeOutcome, TradeTargets, TradeType } from "./types";

/**
 * Percentage change from `from` to `to`, signed for trade direction.
 *
 * A SHORT profits when price falls, so its sign is inverted. Every percentage
 * in the app routes through here; the 2020 code had three mutually
 * inconsistent implementations.
 */
function directionalPercentChange(from: number, to: number, tradeType: TradeType): number {
  if (from === 0) return 0;
  const change = ((to - from) / from) * 100;
  return Number((tradeType === "LONG" ? change : -change).toFixed(2));
}

/* ------------------------------------------------------------------ exits */

/** Total quantity exited across all exits. */
export function exitedQuantity(trade: ClosedTrade): number {
  return trade.exits.reduce((sum, exit) => sum + exit.quantity, 0);
}

/** Quantity still held. Zero for a fully-closed position. */
export function remainingQuantity(trade: Trade): number {
  if (trade.exits.length === 0) return trade.buy.quantity;
  return trade.buy.quantity - trade.exits.reduce((sum, e) => sum + e.quantity, 0);
}

/**
 * Quantity-weighted average exit price.
 *
 * With a single exit this is just that exit's price. It generalises for free
 * when scaling out lands, which is the whole reason exits are a list.
 */
export function averageExitPrice(trade: ClosedTrade): number {
  const quantity = exitedQuantity(trade);
  if (quantity === 0) return trade.exits[0].price;
  return trade.exits.reduce((sum, e) => sum + e.price * e.quantity, 0) / quantity;
}

/** The exit that closed the position. */
export function lastExit(trade: ClosedTrade): ClosedTrade["exits"][number] {
  return trade.exits[trade.exits.length - 1] ?? trade.exits[0];
}

/* ------------------------------------------------------------ percentages */

/**
 * Percentage move from the buy price to `currentPrice`.
 *
 * `currentPrice` is required, deliberately. The 2020 version defaulted to
 * `trade.sell.price`, which on an open position was a null placeholder — so
 * every open trade reported a -100% move.
 */
export function percentDiff(trade: Trade, currentPrice: number): number {
  return directionalPercentChange(trade.buy.price, currentPrice, trade.tradeType);
}

/**
 * The price to measure an open position against, whatever its source.
 *
 * A fed asset uses the live figure and ignores any stored one; a manual asset
 * uses what the owner last entered. Returning `undefined` rather than falling
 * back between the two is deliberate — a stale manual price standing in for a
 * failed lookup would be indistinguishable from a live one on screen.
 */
export function currentPriceFor(trade: Trade, feedPrice?: number): number | undefined {
  return hasPriceFeed(trade) ? feedPrice : trade.lastPrice;
}

/** Percentage move actually realised, against the average exit price. */
export function realisedPercent(trade: ClosedTrade): number {
  return directionalPercentChange(trade.buy.price, averageExitPrice(trade), trade.tradeType);
}

/**
 * Realised percentage, truncated to a whole number for display.
 *
 * The 2020 version wrapped this in `parseInt(...)`, which coerces to a string
 * first — so any value small enough to render exponentially was misread
 * (`parseInt(1e-7)` is `1`, not `0`).
 */
export function calculateExitPercent(trade: ClosedTrade): number {
  if (trade.buy.price === averageExitPrice(trade)) return 0;
  return Math.trunc(realisedPercent(trade));
}

/* ---------------------------------------------------------------- profits */

/**
 * Realised profit in the trade's quote currency.
 *
 * Prefers the figure stored at exit time — it was computed against prices that
 * may no longer be retrievable — and falls back to recomputing across exits.
 */
export function calculateProfit(trade: ClosedTrade): number | null {
  if (trade.profit != null) return trade.profit;

  const { buy, tradeType } = trade;
  const proceeds = trade.exits.reduce((sum, e) => sum + e.price * e.quantity, 0);
  const cost = buy.price * exitedQuantity(trade);
  return formatPrice(tradeType === "LONG" ? proceeds - cost : cost - proceeds);
}

/** Profit the position would realise if closed entirely at its profit target. */
export function calculateTargetProfit(trade: Trade): number | null {
  const { buy, target, tradeType } = trade;
  const gross =
    tradeType === "LONG"
      ? target.profit * buy.quantity - buy.price * buy.quantity
      : buy.price * buy.quantity - target.profit * buy.quantity;
  return formatPrice(gross);
}

/**
 * Percentage moves the profit and stop-loss targets represent.
 *
 * The 2020 implementation carried `TODO: These calculations are wrong` and was
 * still called. Two defects: it divided prices rather than computing a
 * percentage change, and it inferred direction from a heuristic
 * (`target.profit > buy.price`) instead of reading `tradeType`.
 */
export function calculateTargetExitPercentages(trade: Trade): {
  profit: number;
  stopLoss: number;
} {
  const { buy, target, tradeType } = trade;
  return {
    profit: directionalPercentChange(buy.price, target.profit, tradeType),
    stopLoss: directionalPercentChange(buy.price, target.stopLoss, tradeType),
  };
}

/**
 * Reward-to-risk ratio implied by the targets.
 *
 * The single most useful number at the moment of entry, and the 2020 app never
 * showed it. Null when the stop loss implies no risk.
 */
export function riskReward(buyPrice: number, target: TradeTargets): number | null {
  const reward = Math.abs(target.profit - buyPrice);
  const risk = Math.abs(buyPrice - target.stopLoss);
  if (risk === 0 || !Number.isFinite(reward / risk)) return null;
  return Number((reward / risk).toFixed(2));
}

/* --------------------------------------------------------------- outcomes */

/** Whether a closed trade reached its profit target. */
export function hitProfitTarget(trade: ClosedTrade): boolean {
  const price = averageExitPrice(trade);
  return trade.tradeType === "LONG" ? price > trade.target.profit : price < trade.target.profit;
}

/** Whether a closed trade fell through its stop loss. */
export function hitStopLoss(trade: ClosedTrade): boolean {
  const price = averageExitPrice(trade);
  return trade.tradeType === "LONG" ? price < trade.target.stopLoss : price > trade.target.stopLoss;
}

/**
 * How a trade turned out.
 *
 * Measured against *targets*, not the buy price: a trade that closed above its
 * buy price but below its profit target is "between", not a win. That was the
 * 2020 behaviour and it is preserved — it reflects the app's thesis that
 * trading to a plan is what counts.
 */
export function tradeOutcome(trade: Trade): TradeOutcome {
  if (trade.exits.length === 0) return "open";
  const closed = trade as ClosedTrade;
  if (hitProfitTarget(closed)) return "hitProfitTarget";
  if (hitStopLoss(closed)) return "hitStopLoss";
  return "between";
}

/** Whether an open position has moved past its stop loss. */
export function shouldSellLoss(trade: Trade, currentPrice: number): boolean {
  return trade.tradeType === "LONG"
    ? currentPrice < trade.target.stopLoss
    : currentPrice > trade.target.stopLoss;
}

/** Whether an open position has reached its profit target. */
export function shouldSellProfit(trade: Trade, currentPrice: number): boolean {
  return trade.tradeType === "LONG"
    ? currentPrice > trade.target.profit
    : currentPrice < trade.target.profit;
}

/** Days held — to the final exit if closed, otherwise to `now`. */
export function countTradeDays(trade: Trade, now: Date = new Date()): number {
  const end = trade.exits.length === 0 ? now : lastExit(trade as ClosedTrade).date;
  return differenceInDays(end, trade.buy.date);
}

/* ------------------------------------------------------- target modelling */

/** Convert a percentage move into an absolute target price. */
export function targetPriceFromPercent(
  buyPrice: number,
  percent: number,
  tradeType: TradeType,
): number {
  const signed = tradeType === "LONG" ? percent : -percent;
  return buyPrice * (1 + signed / 100);
}

/** Convert an absolute target price back into a percentage move. */
export function percentFromTargetPrice(
  buyPrice: number,
  targetPrice: number,
  tradeType: TradeType,
): number {
  return directionalPercentChange(buyPrice, targetPrice, tradeType);
}

/**
 * Whether targets are the right side of the buy price for the direction.
 *
 * The 2020 form accepted a profit target below the buy price on a LONG and
 * stored it happily, then rendered nonsense percentages from it.
 */
export function validateTargets(
  buyPrice: number,
  target: TradeTargets,
  tradeType: TradeType,
): { profit?: string; stopLoss?: string } {
  const errors: { profit?: string; stopLoss?: string } = {};
  const up = tradeType === "LONG";

  if (up ? target.profit <= buyPrice : target.profit >= buyPrice) {
    errors.profit = up
      ? "A long profits when price rises — the target must be above your buy price."
      : "A short profits when price falls — the target must be below your buy price.";
  }

  if (up ? target.stopLoss >= buyPrice : target.stopLoss <= buyPrice) {
    errors.stopLoss = up
      ? "A stop loss caps a fall — it must sit below your buy price."
      : "A stop loss on a short caps a rise — it must sit above your buy price.";
  }

  return errors;
}
